import { cn } from '@/lib/utils'

import { DiscountBadge } from './DiscountBadge'
import { NewBadge } from './NewBadge'
import { ProductImage } from './ProductImage'
import { ProductName } from './ProductName'
import { ProductPrice } from './ProductPrice'
import { CARD_CONTAINER_CLASSES } from './styles'

import type { ProductCardProps } from '../../types'

const CONTENT_CLASSES = 'grid grid-rows-3 gap-2 h-[135px] xl:h-[185px] w-full'

/**
 * ProductCard - Reusable product card component
 *
 * Composed of smaller, focused components:
 * - DiscountBadge: Shows "- X Lei" discount badge
 * - ProductImage: Main image with hover effect (second image)
 * - ProductName: Truncated product name as link
 * - ProductPrice: Price display with MSRP/discount handling
 * - renderActions: action row supplied by the caller (cart, wishlist)
 */
export function ProductCard({
  product,
  className,
  showDiscountBadge = true,
  priceSuffix = 'cu TVA',
  imageLoading = 'lazy',
  renderActions,
}: ProductCardProps) {
  return (
    <div className={cn(CARD_CONTAINER_CLASSES, className)}>
      {showDiscountBadge && (
        <DiscountBadge
          price={product.price}
          specialPrice={product.specialPrice}
        />
      )}

      <NewBadge isNew={product.isNew} />

      <ProductImage
        name={product.name}
        url={product.url}
        imageUrl={product.imageUrl}
        hoverImageUrl={product.hoverImageUrl}
        imageLoading={imageLoading}
        hasVideo={product.hasVideo}
      />

      <div className={CONTENT_CLASSES}>
        <ProductName name={product.name} url={product.url} />

        <ProductPrice
          price={product.price}
          specialPrice={product.specialPrice}
          msrp={product.msrp}
          priceSuffix={priceSuffix}
        />

        {renderActions?.(product)}
      </div>
    </div>
  )
}
